import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Menu, X } from 'lucide-react'

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50)
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const navLinks = [
    { name: 'HOME', href: '#home' },
    { name: 'ABOUT', href: '#about' },
    { name: 'PRODUCTS', href: '#products' },
    { name: 'PLATFORMS', href: '#platforms' },
    { name: 'CONTACT', href: '#contact' },
  ]

  const scrollToSection = (href) => {
    const element = document.querySelector(href)
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' })
    }
    setActiveSection(href.substring(1)) // Update active section
    setIsMenuOpen(false)
  }

  const LogoIcon = () => (
    <img 
    src="/DailyStrideLogo.svg"
    alt="Daily Stride Logo"
    className="w-12 h-12"
    />
  )

  {/** Track active section */}
  const [activeSection, setActiveSection] = useState('home');
  useEffect(() => {
    const sections = navLinks.map(link => link.href.substring(1))
    
    const handleScrollSpy = () => {
      for (const section of sections) {
        const element = document.getElementById(section)
        if (element) {
          const rect = element.getBoundingClientRect()
          if (rect.top <= 150 && rect.bottom >= 150) {
            setActiveSection(section)
            break
          }
        }
      }
    }

    window.addEventListener('scroll', handleScrollSpy)
    return () => window.removeEventListener('scroll', handleScrollSpy)
  }, [])

  return (
    <>
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-ds-navy/95 backdrop-blur-md shadow-lg' : 'bg-ds-navy'
        }`}
      >
        <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-12">
          <div className="flex items-center justify-between h-16 sm:h-20 lg:h-24">
            {/* Logo */}
            <motion.div 
              className="flex items-center justify-center gap-2 sm:gap-3"
              whileHover={{ scale: 1.02 }}
            >
              <LogoIcon />
              <span className="font-heading font-bold text-lg sm:text-xl lg:text-2xl tracking-wide text-ds-blue mt-1">
                DAILY STRIDE
              </span>
            </motion.div>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-6 xl:gap-10">
              {navLinks.map((link, index) => (
                <motion.button
                  key={link.name}
                  onClick={() => scrollToSection(link.href)}
                  className={`font-body text-sm tracking-wider transition-colors duration-300 ${
                    activeSection === link.href.substring(1)
                      ? 'text-ds-blue border-b-2 border-ds-blue pb-1' 
                      : 'text-[#fbfbfb] hover:text-ds-blue'
                  }`}
                  whileHover={{ y: -2 }}
                  transition={{ duration: 0.2 }}
                >
                  {link.name}
                </motion.button>
              ))}
            </div>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden text-white p-2"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label="Toggle menu"
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 lg:hidden"
          >
            {/* Backdrop */}
            <div 
              className="absolute inset-0 bg-ds-navy/90"
              onClick={() => setIsMenuOpen(false)}
            />
            
            {/* Menu Content */}
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
              className="absolute top-0 right-0 w-full h-full bg-ds-navy flex flex-col"
            >
              {/* Mobile Header */}
              <div className="flex items-center justify-between px-4 sm:px-6 h-16 sm:h-20 border-b border-ds-navy-light">
                <div className="flex items-center gap-2 sm:gap-3">
                  <svg viewBox="0 0 48 48" fill="none" className="w-8 h-8 sm:w-10 sm:h-10">
                    <path d="M24 4L8 12V24C8 34 16 42 24 44C32 42 40 34 40 24V12L24 4Z" fill="none" stroke="#4a7dbd" strokeWidth="2"/>
                    <path d="M24 14L18 20L24 26L18 32" stroke="#4a7dbd" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M24 14L30 20L24 26L30 32" stroke="#4a7dbd" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                    <circle cx="24" cy="20" r="2" fill="#4a7dbd"/>
                  </svg>
                  <span className="font-heading font-bold text-lg sm:text-xl tracking-wide text-ds-blue">
                    DAILY STRIDE
                  </span>
                </div>
                <button
                  onClick={() => setIsMenuOpen(false)}
                  className="text-white p-2"
                  aria-label="Close menu"
                >
                  <X size={24} />
                </button>
              </div>

              {/* Menu Links */}
              <div className="flex-1 px-4 sm:px-6 pt-6 sm:pt-8">
                <div className="bg-ds-navy-light rounded-2xl p-4 sm:p-6">
                  {navLinks.map((link, index) => (
                    <motion.button
                      key={link.name}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                      onClick={() => scrollToSection(link.href)}
                      className={`block w-full text-left py-4 sm:py-5 font-heading text-sm sm:text-base tracking-wider transition-colors ${
                        activeSection === link.href.substring(1)
                          ? 'text-ds-blue' 
                          : 'text-white hover:text-ds-blue'
                      } ${index !== navLinks.length - 1 ? 'border-b border-ds-navy/50' : ''}`}
                    >
                      {link.name}
                    </motion.button>
                  ))}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}

export default Navbar
